/**
 * VÉRAULT Member Suite — UploadDocumentModal (Wave 27.5 · Phase 4e).
 * ────────────────────────────────────────────────────────────────────
 * Calm in-portal upload widget for `upload_request` documents.
 * Submits multipart to POST /api/client-portal/documents/{id}/upload.
 *
 *   Drag-and-drop · click-to-browse · live progress · graceful errors.
 *   10 MB max enforced client-side AND server-side.
 */
import React, { useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Upload, FileText, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../../../index";
import { useOnlineState } from "../../../../components/OnlineProvider";

const MAX_BYTES = 10 * 1024 * 1024; // 10 MB

function fmtSize(n) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export default function UploadDocumentModal({ doc, onClose, onUploaded }) {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [progress, setProgress] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const { isOnline } = useOnlineState();

  const handleFilePick = (picked) => {
    if (!picked) return;
    if (picked.size > MAX_BYTES) {
      toast.error("File too large", { description: "Maximum 10 MB." });
      return;
    }
    setFile(picked);
    setProgress(0);
  };

  const handleUpload = async () => {
    if (!file || submitting) return;
    setSubmitting(true);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await api.post(
        `/client-portal/documents/${doc.id}/upload`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (evt) => {
            if (evt.total) {
              setProgress(Math.round((evt.loaded / evt.total) * 100));
            }
          },
        },
      );
      toast.success("Upload received", {
        description: `${file.name} · ${fmtSize(file.size)}`,
      });
      onUploaded?.(res.data);
      onClose();
    } catch (err) {
      const detail = err?.response?.data?.detail || "Couldn't upload the file.";
      toast.error("Upload failed", { description: detail });
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
        className="fixed inset-0 z-50 bg-black/65 backdrop-blur-md flex items-center justify-center p-4 sm:p-8"
        onClick={onClose}
        data-testid="upload-document-modal"
      >
        <motion.div
          initial={{ opacity: 0, y: 12, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 8, scale: 0.98 }}
          transition={{ duration: 0.35 }}
          onClick={(e) => e.stopPropagation()}
          className="relative w-full max-w-lg rounded-lg overflow-hidden bg-[hsl(var(--background))] shadow-2xl"
        >
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="absolute top-4 right-4 inline-flex items-center justify-center size-9 rounded-full hover:bg-[hsl(var(--background-paper))]"
          >
            <X className="size-4" strokeWidth={1.5} />
          </button>

          <div className="px-7 sm:px-9 py-8">
            <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--foreground-muted))]">
              Upload requested
            </p>
            <h2 className="mt-2 font-serif text-[22px] leading-tight tracking-tight">
              {doc.title || "Document upload"}
            </h2>

            {/* Drop zone */}
            <div
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragOver(false);
                handleFilePick(e.dataTransfer.files?.[0]);
              }}
              onClick={() => fileRef.current?.click()}
              className={`mt-5 rounded-md border-2 border-dashed px-6 py-8 text-center cursor-pointer transition-colors ${
                dragOver
                  ? "border-[hsl(var(--accent-gold))] bg-[hsl(var(--accent-gold)/0.06)]"
                  : "border-[hsl(var(--border))] hover:border-[hsl(var(--accent-gold)/0.5)] bg-white/40"
              }`}
              data-testid="upload-dropzone"
            >
              <input
                ref={fileRef}
                type="file"
                className="hidden"
                onChange={(e) => handleFilePick(e.target.files?.[0])}
                data-testid="upload-file-input"
              />
              {!file ? (
                <>
                  <Upload className="mx-auto size-7 text-[hsl(var(--foreground-muted))]" strokeWidth={1.4} />
                  <p className="mt-3 font-serif text-[16px] text-[hsl(var(--foreground))]">
                    Drop your file here, or click to browse
                  </p>
                  <p className="mt-1 font-accent italic text-[12.5px] text-[hsl(var(--foreground-muted))]">
                    PDF, JPG, PNG · 10 MB max
                  </p>
                </>
              ) : (
                <div className="flex items-center justify-center gap-3" data-testid="upload-file-preview">
                  <FileText className="size-5 text-[hsl(var(--accent-gold))]" strokeWidth={1.5} />
                  <span className="font-serif text-[15px] text-[hsl(var(--foreground))]">
                    {file.name}
                  </span>
                  <span className="font-mono text-[11px] text-[hsl(var(--foreground-muted))]">
                    {fmtSize(file.size)}
                  </span>
                </div>
              )}
            </div>

            {/* Progress bar */}
            {submitting && (
              <div className="mt-5">
                <div className="h-[2px] w-full bg-[hsl(var(--border))] rounded-full overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${progress}%` }}
                    transition={{ duration: 0.3 }}
                    className="h-full bg-[hsl(var(--accent-gold))]"
                    data-testid="upload-progress"
                  />
                </div>
                <p className="mt-2 font-mono text-[10px] text-[hsl(var(--foreground-muted))] tabular-nums">
                  {progress < 100 ? `Uploading · ${progress}%` : (
                    <span className="inline-flex items-center gap-1">
                      <Check className="size-3" strokeWidth={2} />
                      Received
                    </span>
                  )}
                </p>
              </div>
            )}

            {/* Actions */}
            <div className="mt-6 flex flex-wrap items-center gap-2 justify-end">
              {!isOnline ? (
                <span
                  className="mr-auto text-[11px] italic text-[hsl(var(--foreground-muted))]"
                  data-testid="upload-offline-hint"
                >
                  Available when you reconnect.
                </span>
              ) : null}
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 rounded-md font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))]"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleUpload}
                disabled={!file || submitting || !isOnline}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md font-mono text-[10px] uppercase tracking-[0.22em] bg-[hsl(var(--foreground))] text-[hsl(var(--background))] disabled:opacity-40 hover:bg-[hsl(var(--accent-gold))] transition-colors"
                data-testid="upload-submit"
              >
                {submitting && <Loader2 className="size-3 animate-spin" strokeWidth={2} />}
                Upload file
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
